import { useEffect, useRef } from 'react';
import './motion.css';

// Shared progressive enhancement. Observes newly filtered/resized content as well.
export default function SiteMotion({ children }) {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const desktop = window.matchMedia('(min-width: 1024px) and (pointer: fine)');
    const reveals = new Set();
    const ambient = new Set();
    let decorations = [];
    let revealObserver;
    let ambientObserver;
    let frame = 0;

    const updateParallax = () => {
      frame = 0;
      decorations.forEach(element => {
        const section = element.closest('section');
        if (!section) return;
        const bounds = section.getBoundingClientRect();
        if (bounds.bottom < 0 || bounds.top > window.innerHeight) return;
        const progress = Math.max(-1, Math.min(1,
          (window.innerHeight / 2 - bounds.top - bounds.height / 2) /
          ((window.innerHeight + bounds.height) / 2)
        ));
        element.style.setProperty('--parallax-y', `${progress * Number(element.dataset.parallax)}px`);
      });
    };
    const scheduleParallax = () => {
      if (!frame) frame = window.requestAnimationFrame(updateParallax);
    };
    const stopParallax = () => {
      window.removeEventListener('scroll', scheduleParallax);
      window.removeEventListener('resize', scheduleParallax);
      window.cancelAnimationFrame(frame);
      frame = 0;
      decorations.forEach(element => element.style.removeProperty('--parallax-y'));
    };

    const refreshTargets = () => {
      reveals.forEach(element => {
        if (!root.contains(element)) {
          revealObserver?.unobserve(element);
          reveals.delete(element);
        }
      });
      ambient.forEach(element => {
        if (!root.contains(element)) {
          ambientObserver?.unobserve(element);
          ambient.delete(element);
        }
      });
      root.querySelectorAll('[data-reveal]').forEach(element => {
        if (reveals.has(element)) return;
        reveals.add(element);
        // Steps opt into a short sequence even when the first row is visible on load.
        // Content above a restored scroll position or at a direct anchor stays readable.
        const bounds = element.getBoundingClientRect();
        const initialStep = element.dataset.reveal === 'step' && !window.location.hash && bounds.bottom > 0;
        if (!revealObserver || element.dataset.revealed || (bounds.top < window.innerHeight && !initialStep)) return;
        element.classList.add('reveal-pending');
        revealObserver.observe(element);
      });
      root.querySelectorAll('[data-ambient]').forEach(element => {
        if (ambient.has(element)) return;
        ambient.add(element);
        ambientObserver?.observe(element);
      });
      decorations = [...root.querySelectorAll('.home-page [data-parallax]')];
    };

    const configure = () => {
      revealObserver?.disconnect();
      ambientObserver?.disconnect();
      revealObserver = undefined;
      ambientObserver = undefined;
      stopParallax();
      reveals.forEach(element => element.classList.remove('reveal-pending'));
      reveals.clear();
      ambient.clear();

      if (!reducedMotion.matches && 'IntersectionObserver' in window) {
        revealObserver = new IntersectionObserver(entries => {
          entries.forEach(({ target, isIntersecting }) => {
            if (!isIntersecting) return;
            target.classList.remove('reveal-pending');
            target.dataset.revealed = 'true';
            revealObserver?.unobserve(target);
          });
        }, { threshold: 0.08 });
        ambientObserver = new IntersectionObserver(entries => {
          entries.forEach(({ target, isIntersecting }) => {
            target.dataset.motionActive = String(isIntersecting);
          });
        });
      }
      refreshTargets();
      if (!reducedMotion.matches && desktop.matches) {
        window.addEventListener('scroll', scheduleParallax, { passive: true });
        window.addEventListener('resize', scheduleParallax);
        scheduleParallax();
      }
    };

    const onVisibilityChange = () => {
      root.dataset.motionPaused = String(document.hidden);
    };
    configure();
    onVisibilityChange();
    const mutations = new MutationObserver(refreshTargets);
    mutations.observe(root, { childList: true, subtree: true });
    document.addEventListener('visibilitychange', onVisibilityChange);
    reducedMotion.addEventListener('change', configure);
    desktop.addEventListener('change', configure);
    return () => {
      mutations.disconnect();
      revealObserver?.disconnect();
      ambientObserver?.disconnect();
      stopParallax();
      reveals.forEach(element => element.classList.remove('reveal-pending'));
      reducedMotion.removeEventListener('change', configure);
      desktop.removeEventListener('change', configure);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return <div ref={rootRef} className="site-motion">{children}</div>;
}
